import { useCallback, useEffect, useRef, useState } from 'react';

type SetValue<T> = (value: T | ((val: T) => T)) => void;

type Listener = (value: unknown) => void;

/**
 * Live subscribers per storage key. `storage` events only reach OTHER tabs, so
 * without this every hook instance sharing a key keeps its own stale copy until
 * a reload — which shows up as sibling grids drifting apart (a grouped list
 * renders one header grid plus a body grid per group off one set of columns, so
 * a column resized in the header must reach the bodies immediately).
 */
const listenersByKey = new Map<string, Set<Listener>>();

function subscribe(key: string, listener: Listener): () => void {
  const listeners = listenersByKey.get(key) ?? new Set<Listener>();
  listeners.add(listener);
  listenersByKey.set(key, listeners);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      listenersByKey.delete(key);
    }
  };
}

function publish(key: string, value: unknown, source: Listener): void {
  listenersByKey.get(key)?.forEach((listener) => {
    if (listener !== source) {
      listener(value);
    }
  });
}

function readValue<T>(key: string | null, initialValue: T): T {
  if (!key) {
    return initialValue;
  }
  try {
    const stored = localStorage.getItem(key);
    return stored ? (JSON.parse(stored) as T) : initialValue;
  } catch {
    // Corrupt/legacy JSON under this key: fall back instead of throwing mid-render.
    return initialValue;
  }
}

/**
 * Persists state to local storage. A `null` key disables persistence entirely
 * (no read, no write) — useful for conditionally-persisted consumers. When the
 * key changes, state re-reads from the new key rather than writing the previous
 * key's value into it. Storage is written only after an actual change, so merely
 * mounting a consumer never creates an entry. Every mounted consumer of the same
 * key stays in sync within the tab: a write fans out to the others, which adopt
 * the value without re-writing it.
 */
export function useLocalStorage<T>(
  key: string | null,
  initialValue: T,
): [T, SetValue<T>] {
  const [state, setState] = useState<{ key: string | null; value: T }>(() => ({
    key,
    value: readValue(key, initialValue),
  }));
  const hasChanges = useRef(false);

  // Adjust-state-during-render: when the key changes, re-read from storage in
  // the same render so the write effect below never persists the old key's
  // value under the new key.
  if (state.key !== key) {
    setState({ key, value: readValue(key, initialValue) });
  }

  // Mirrors of the committed state, so `setValue` can stay identity-stable while
  // still resolving updater functions and publishing against current values.
  const keyRef = useRef(key);
  keyRef.current = key;
  const valueRef = useRef(state.value);
  valueRef.current = state.value;

  const receive = useCallback((value: unknown): void => {
    valueRef.current = value as T;
    setState({ key: keyRef.current, value: value as T });
  }, []);

  useEffect(() => {
    if (!key) {
      return;
    }
    return subscribe(key, receive);
  }, [key, receive]);

  useEffect(() => {
    if (!key || !hasChanges.current) {
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify(state.value));
    } catch (error) {
      // Safari private mode and a full quota both throw here. Losing the
      // persisted preference is acceptable; an exception escaping an effect
      // would unmount the tree around it.
      console.warn(`Could not persist "${key}" to local storage`, error);
    }
  }, [key, state.value]);

  const setValue = useCallback<SetValue<T>>(
    (value) => {
      const next =
        value instanceof Function ? value(valueRef.current) : (value as T);
      hasChanges.current = true;
      valueRef.current = next;
      setState({ key: keyRef.current, value: next });

      if (keyRef.current) {
        publish(keyRef.current, next, receive);
      }
    },
    [receive],
  );

  return [state.value, setValue];
}
